import { injectIntl } from "react-intl";
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { createSelector } from 'reselect';
import { deleteRule, getFeeschedule, getGroups, getRules, ruleReset } from '../../../../../store/actions/contracts';
import { set } from '../../../../../store/actions/pages';
import { changePrecedence, disableRule, enableDisableRuleReset, enableRule, setCloneRuleDialog } from '../../../../../store/actions/rules';
import { CONFIG_TYPE_EAPG, CONFIG_TYPE_IPPS, CONFIG_TYPE_OPPS, CONFIG_TYPE_SNF, CONFIG_TYPE_IRF, CONFIG_TYPE_IPF, RULE_TYPE_CARVEOUT, RULE_TYPE_DEFAULT, RULE_TYPE_RULE } from '../../../../../store/constants/contract';
import { dateEquators } from '../../../../../store/constants/pages';
import { selectRuleStatus } from '../../../../../store/selectors/rules';
import { createContractRuleTypeField,createPayValueDataField, createPayMethodDataField, createPlainDateField, createSecondLevelField, createStringDataField } from '../../../../../util/format';
import { createFilteredSortedDataSelector } from '../../../../../util/selector';
import RuleViewComponent from './RuleView';
import { selectCanAccessCMS } from "../../../../../store/selectors/access";

const DEFAULT_SORT = [{ id: 'name', orderBy: 'asc' }];

const selectFields = createSelector(
    () => [
        createStringDataField('name', 'Name'),
        createContractRuleTypeField('ruleType', 'Rule Type', 'name'),
        createPayMethodDataField('paymethod', 'Pay Method'),
        createPayValueDataField('payvalue', 'Pay Value'),
        createSecondLevelField('paySource', 'Pay Source', 'name'),
        createPlainDateField('fromDate', 'From Date', { equators: dateEquators, whiteSpace: 'nowrap' }),
        createPlainDateField('toDate', 'To Date', { equators: dateEquators, whiteSpace: 'nowrap' }),
        createSecondLevelField('ruleSelectionCriteriaXref', 'Criteria', 'length'),
    ]
);

export const selectRulesList = (state) => state.contracts.rules;
const selectContractData = (state) => state.contracts.data;
const selectServiceTypeObj = (state, props) => props.serviceTypeObj;

const selectRules = createSelector(
    (state, props) => props.serviceId || [],
    selectRulesList,
    selectRuleStatus,
    (serviceId, rules, status) => {
        const rulesList = rules[`rules-${serviceId}`] ?? [];
        if (status) {
            return rulesList.filter(item => item.active);
        }
        return rulesList
    }
);

const selectFilteredFields = createSelector(
    selectFields,
    (fields) => {
        return fields.map(field => field.id);
    }
);

const selectSort = createSelector(
    (state, props) => state.pages[`${props.serviceId}rules-landing`] ? state.pages[`${props.serviceId}rules-landing`].sort : null,
    (sort) => sort || DEFAULT_SORT,
);

const selectFilteredData = createFilteredSortedDataSelector(
    selectFilteredFields,
    (state, props) => state.pages[`${props.serviceId}rules-landing`] ? state.pages[`${props.serviceId}rules-landing`].filter : null,
    selectSort,
    selectRules,
);

const selectFormattedData = createSelector(selectFilteredData, selectContractData, selectServiceTypeObj, (items, contractData, serviceType) => {
    let contract = contractData[0];
    // Sort with Precedence
    const precedenceSort = items.sort((a, b) => {
        if (a.precedence < b.precedence) {
            return -1;
        }
        if (a.precedence > b.precedence) {
            return 1;
        }
        return 0;
    })

    let ppsConfigurationRules = [];
    let eapgConfigurationRules = [];
    if(contract) {
        if(serviceType && serviceType.name === 'INPATIENT') {
            if(contract.drgconfigurationToContractXref && contract.drgconfigurationToContractXref.length > 0) {
                contract.drgconfigurationToContractXref.forEach(item => {
                    let tempRule = {
                        id:item.id,
                        configId: item.drgConfiguration?.id ?? null,
                        name: item.name ?? "IPPS",
                        ruleType: {id: 0, name: CONFIG_TYPE_IPPS},
                        paymethod: {id: 0, name: "Percentage"},
                        payvalue: item.percentage,
                        paySource: {id: 0, name: "N/A"},
                        fromDate: item.effectiveFrom ?? null,
                        toDate: null,
                        hideDragAndDrop: true,
                        hideCollapsible: true,
                        hideActionsExceptEdit: true
                    };
                    ppsConfigurationRules.push(tempRule);
                });
            }
            if(contract.contractSnfconfigurationXref && contract.contractSnfconfigurationXref.length > 0) {
                contract.contractSnfconfigurationXref.forEach(item => {
                    let tempRule = {
                        id:item.id,
                        configId: null,
                        name: item.name ?? "SNF",
                        ruleType: {id: 0, name: CONFIG_TYPE_SNF},
                        paymethod: {id: 0, name: "Percentage"},
                        payvalue: item.percentage,
                        paySource: {id: 0, name: "N/A"},
                        fromDate: item.effectiveFrom ?? null,
                        toDate: null,
                        hideDragAndDrop: true,
                        hideCollapsible: true,
                        hideActionsExceptEdit: true
                    };
                    ppsConfigurationRules.push(tempRule);
                });
            }
            if(contract.contractIrfconfigurationXref && contract.contractIrfconfigurationXref.length > 0) {
                contract.contractIrfconfigurationXref.forEach(item => {
                    let tempRule = {
                        id:item.id,
                        configId: null,
                        name: item.name ?? "IRF",
                        ruleType: {id: 0, name: CONFIG_TYPE_IRF},
                        paymethod: {id: 0, name: "Percentage"},
                        payvalue: item.percentage,
                        paySource: {id: 0, name: "N/A"},
                        fromDate: item.effectiveFrom ?? null,
                        toDate: null,
                        hideDragAndDrop: true,
                        hideCollapsible: true,
                        hideActionsExceptEdit: true
                    };
                    ppsConfigurationRules.push(tempRule);
                });
            }
            if(contract.contractIpfconfigurationXref && contract.contractIpfconfigurationXref.length > 0) {
                contract.contractIpfconfigurationXref.forEach(item => {
                    let tempRule = {
                        id:item.id,
                        configId: null,
                        name: item.name ?? "IPF",
                        ruleType: {id: 0, name: CONFIG_TYPE_IPF},
                        paymethod: {id: 0, name: "Percentage"},
                        payvalue: item.percentage,
                        paySource: {id: 0, name: "N/A"},
                        fromDate: item.effectiveFrom ?? null,
                        toDate: null,
                        hideDragAndDrop: true,
                        hideCollapsible: true,
                        hideActionsExceptEdit: true
                    };
                    ppsConfigurationRules.push(tempRule);
                });
            }
        }
        else if(serviceType && serviceType.name === 'OUTPATIENT' && contract.contractOppsconfigurationXref && contract.contractOppsconfigurationXref.length > 0) {
            contract.contractOppsconfigurationXref.forEach(item => {
                let tempRule = {
                    id:item.id,
                    configId: item.oppsConfiguration?.id ?? null,
                    name: item.name ?? "OPPS",
                    ruleType: {id: 0, name: CONFIG_TYPE_OPPS },
                    paymethod: {id: 0, name: "Percentage"},
                    payvalue: item.percentage,
                    paySource: {id: 0, name: "N/A"},
                    fromDate: item.effectiveFrom ?? null,
                    toDate: null,
                    hideDragAndDrop: true,
                    hideCollapsible: true,
                    hideActionsExceptEdit: true
                };
                ppsConfigurationRules.push(tempRule);
            });
        }
        else if(serviceType && serviceType.name === 'OUTPATIENT' && contract.contractEapgconfigurationXref && contract.contractEapgconfigurationXref.length > 0) {
            contract.contractEapgconfigurationXref.forEach(item => {
                let tempRule = {
                    id:item.id,
                    configId: item.eapgConfiguration?.id ?? null,
                    name: item.name ?? 'EAPG',
                    ruleType: {id: 0, name: CONFIG_TYPE_EAPG },
                    paymethod: {id: 0, name: "Percentage"},
                    payvalue: item.percentage,
                    paySource: {id: 0, name: "N/A"},
                    fromDate: item.effectiveFrom ?? null,
                    toDate: null,
                    hideDragAndDrop: true,
                    hideCollapsible: true,
                    hideActionsExceptEdit: true
                };
                eapgConfigurationRules.push(tempRule);
            });
        }
    }

    // Create grouping of rules
    const carveOutRulesAbovePPS = precedenceSort.filter(d => d.ruleType.name === RULE_TYPE_CARVEOUT && d.abovePPS === true);
    const carveOutRules = precedenceSort.filter(d => d.ruleType.name === RULE_TYPE_CARVEOUT && d.abovePPS === false);
    const typeRules = precedenceSort.filter(d => d.ruleType.name === RULE_TYPE_RULE);
    const defaultRules = precedenceSort.filter(d => d.ruleType.name === RULE_TYPE_DEFAULT);
    const allRulesMapping= [...carveOutRulesAbovePPS, ...ppsConfigurationRules, ...eapgConfigurationRules, ...carveOutRules, ...typeRules, ...defaultRules];
    
    return allRulesMapping?.map((item) => {
        if (item?.useSplitPayment) {
            // SS02-616: Get payvalue from group-based architecture (Schedule) or direct collection (Charge)
            let payvalue = null;

            // Check for groups first (Schedule pay source)
            if (item?.ruleFeeScheduleGroupXref && Array.isArray(item.ruleFeeScheduleGroupXref) && item.ruleFeeScheduleGroupXref.length > 0) {
                // Extract from first group's split array
                const firstGroup = item.ruleFeeScheduleGroupXref[0];
                if (firstGroup?.ruleSplitPaymentXRef && Array.isArray(firstGroup.ruleSplitPaymentXRef) && firstGroup.ruleSplitPaymentXRef.length > 0) {
                    payvalue = firstGroup.ruleSplitPaymentXRef[0]?.payValue;
                }
            } else {
                // Fall back to direct collection (Charge pay source)
                payvalue = item?.ruleSplitPaymentXRef?.[0]?.payValue ?? item?.ruleSplitPaymentXref?.[0]?.payValue;
            }

            return {
                ...item,
                paymethod: { id: 0, name: "Split Unit" },
                payvalue: payvalue,
            };
        }
        return item;
    });
})

export const RuleView = connect(
    () => {
        return (state, props) => ({
            // Rule
            rulesListPending: state.contracts.rulesList.pending,
            fields: selectFields(state as never),
            filteredData: selectFormattedData(state, props),
            order: state.pages[`${props.serviceId}rules-landing`] ? state.pages[`${props.serviceId}rules-landing`].order : null,
            filter: state.pages[`${props.serviceId}rules-landing`] ? state.pages[`${props.serviceId}rules-landing`].filter : null,
            sort: selectSort(state, props),
            codeServices: state.services.data,
            // fee schedule
            feeSchedule: state.contracts.feeSchedule,
            feeSchedulePending: state.contracts.feeScheduleList.pending,

            //enable/disable
            ruleEnablePending: state.rules.enableRule.pending,

            ruleDisablePending: state.rules.disableRule.pending,

            ruleChangePrecedencePending: state.rules.changePrecedence.pending,
            canAccessCMS: selectCanAccessCMS(state as never) || false
        });
    },
    (dispatch, props) => ({
        actions: bindActionCreators({
            getRules: getRules,
            getFeeschedule: getFeeschedule,
            getGroups: getGroups,
            deleteRule: deleteRule,
            setFilter: (value) => set(`${props.serviceId}rules-landing`, 'filter', value),
            setOrder: (value) => set(`${props.serviceId}rules-landing`, 'order', value),
            setSort: (value) => set(`${props.serviceId}rules-landing`, 'sort', value),
            setGlobalConfigsFilters: (value) => set('global-feeschedule-items-landing', 'filter', value),
            ruleReset: ruleReset,
            disableRule: disableRule,
            enableRule: enableRule,
            enableDisableRuleReset: enableDisableRuleReset,
            changePrecedence: changePrecedence,
            setCloneRuleDialog: setCloneRuleDialog
        }, dispatch)
    })
)(injectIntl(RuleViewComponent));
